import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowLeft, ChevronRight, X } from 'lucide-react'
import { useWebHaptics } from 'web-haptics/react'
import { Separator } from 'tailwind-v4/components/separator'
import { useAnySheetOpen } from 'tailwind-v4/lib/sheet-state'
import {
  isHiddenOnMobile,
  isNavItemVisible,
  resolveHref,
  resolveLabel,
  type NavContext,
  type NavItem,
} from './config'
import { openSupportDock } from '@/components/support-dock'
import { useLongPress } from '@/components/support-dock/use-long-press'
import { can } from '@/access/access.policy'
import { OrgSwitcher } from './org-switcher'
import { VenueSwitcher } from './venue-switcher'
import { SquareEntityPicker } from './square-entity-picker'
import { AccountAvatar } from './account-avatar'
import { useAccountMenu, type AccountMenuItem } from './use-account-menu'
import { OldSchemePicker } from '@/pages/[org_id]/nav/old-scheme-picker'
import useUser from '@/hooks/useUser'
import { openCreateReviewPage } from '@/components/Reviews/create-review-page-dialog'
import { preloadRouteByHref } from '@/routes'

const HAPTIC_TAP = [{ duration: 12 }]
const HAPTIC_OPEN = [{ duration: 18 }]
const HAPTIC_CLOSE = [{ duration: 10 }]
const HAPTIC_DRILL = [{ duration: 8 }, { delay: 30, duration: 14 }]
// Back is the reverse of drill-in so forward/back feel mirrored.
const HAPTIC_BACK = [{ duration: 14 }, { delay: 30, duration: 8 }]
/**
 * The buzz that says the hold has armed, fired while the finger is still
 * down. Longer and firmer than a tap on purpose: it is the only warning
 * before a microphone opens, so it has to feel like a different event.
 */
const HAPTIC_VOICE = [{ duration: 26 }]

/**
 * Two-line burger for the floating pill — lucide's `Menu` is three lines;
 * two, generously spaced, reads cleaner at this size (matches the
 * Vercel-style pill). The lines sit at a third / two-thirds of the box
 * (y 8 / 16) so they're symmetric about the centre — i.e. vertically
 * centred against the "Menu" label beside them. lucide stroke language
 * (width 2, round caps, currentColor).
 */
function MenuLines({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden
    >
      <line x1="3.5" y1="8" x2="20.5" y2="8" />
      <line x1="3.5" y1="16" x2="20.5" y2="16" />
    </svg>
  )
}

/**
 * True while a JosefSheet (tailwind-ui) is open. JosefSheet portals an overlay
 * with class `josef-sheet-overlay` onto <body>, so we watch body for it — this
 * lets the floating mobile nav step aside for JosefSheets too (not just the
 * tailwind-v4 Sheets tracked by `useAnySheetOpen`).
 */
function useJosefSheetOpen(): boolean {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const check = () => {
      setOpen(Boolean(document.querySelector('.josef-sheet-overlay')))
    }
    check()
    const observer = new MutationObserver(check)
    observer.observe(document.body, { childList: true })
    return () => {
      observer.disconnect()
    }
  }, [])
  return open
}

/**
 * Is the support voice orb up?
 *
 * It floats bottom-centre, exactly where the menu pill sits, and the orb is
 * the only thing the person is looking at while they talk. Detected off a
 * body attribute rather than shared state because the dock mounts in the
 * organisation layout and this renders from the nav tree, so there is no
 * provider between them — the same reason `useJosefSheetOpen` watches the
 * DOM instead of a store.
 */
function useSupportVoiceOpen(): boolean {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const check = () => {
      setOpen(document.body.dataset.supportVoice === 'on')
    }
    check()
    const observer = new MutationObserver(check)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-support-voice'],
    })
    return () => {
      observer.disconnect()
    }
  }, [])
  return open
}

function isActive(
  pathname: string,
  href: string | undefined,
  mode: 'prefix' | 'exact' = 'prefix'
): boolean {
  if (!href) return false
  if (href === pathname) return true
  if (mode === 'exact') return false
  return pathname.startsWith(`${href}/`)
}

/** Depth-first lookup by id across the whole nav tree (any nesting level). */
function findNavItem(items: NavItem[], id: string): NavItem | null {
  for (const item of items) {
    if (item.id === id) return item
    const found = item.children?.length ? findNavItem(item.children, id) : null
    if (found) return found
  }
  return null
}

/**
 * The id-path of section ancestors whose subtree matches the pathname, so
 * opening the menu can land on the deepest matching group (e.g.
 * `['wi-fi', 'wifi-snapguard']`) rather than only the top-level section.
 */
function findActivePath(
  items: NavItem[],
  matches: (href: string | undefined) => boolean,
  resolve: (item: NavItem) => string | undefined
): string[] {
  for (const item of items) {
    if (!item.children?.length) continue
    const childPath = findActivePath(item.children, matches, resolve)
    if (childPath.length) return [item.id, ...childPath]
    const selfMatch = matches(resolve(item))
    const childMatch = item.children.some((c) => matches(resolve(c)))
    if (selfMatch || childMatch) return [item.id]
  }
  return []
}

/** The one row shape in the panel — nav rows and account rows share it. */
const ROW_CLASS =
  'flex w-full min-h-11 items-center gap-3 rounded-md px-3 py-2.5 text-[15px] transition-colors active:bg-[var(--foreground)]/5'
const ROW_ICON_CLASS = 'size-[18px] shrink-0 opacity-70'

/**
 * A row in the mobile panel's Account view. Same shape as `MobileNavRow`, but
 * driven by `AccountMenuItem` (the shared account menu) rather than a NavItem —
 * this is what makes Profile → Log out reachable on a phone, where the sidebar
 * footer that hosts the desktop menu can't be opened at all.
 */
function AccountRow({
  item,
  onNavigate,
  onAction,
}: {
  item: AccountMenuItem
  /** Link taps close the panel; actions keep it open for their dialog. */
  onNavigate: () => void
  onAction: () => void
}) {
  const Icon = item.icon
  const textClass =
    item.tone === 'destructive'
      ? 'text-destructive'
      : 'text-[var(--foreground)]/90'
  const className = `${ROW_CLASS} ${textClass}`
  const label = <span className="flex-1 truncate text-left">{item.label}</span>

  if (item.kind === 'action') {
    return (
      <button
        type="button"
        data-nav-row
        onClick={() => {
          onAction()
          item.onSelect()
        }}
        className={className}
      >
        <Icon className={ROW_ICON_CLASS} />
        {label}
      </button>
    )
  }

  if (item.kind === 'external') {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noreferrer"
        data-nav-row
        onClick={onNavigate}
        className={className}
      >
        <Icon className={ROW_ICON_CLASS} />
        {label}
      </a>
    )
  }

  return (
    <Link to={item.to} data-nav-row onClick={onNavigate} className={className}>
      <Icon className={ROW_ICON_CLASS} />
      {label}
    </Link>
  )
}

function MobileNavRow({
  item,
  ctx,
  activePathname,
  exactActive,
  onSelect,
  onDrillInto,
}: {
  item: NavItem
  ctx: NavContext
  activePathname: string
  exactActive?: boolean
  onSelect: () => void
  onDrillInto: (item: NavItem) => void
}) {
  const href = resolveHref(item, ctx)
  const label = resolveLabel(item, ctx)
  const hasChildren = Boolean(item.children?.length)
  const active = exactActive
    ? Boolean(href) && href === activePathname
    : isActive(activePathname, href, item.activeMatch)

  const isDestructive = item.tone === 'destructive'
  const rowClass = ROW_CLASS
  const textClass = isDestructive
    ? 'text-destructive'
    : active
      ? 'text-[var(--foreground)]'
      : 'text-[var(--foreground)]/90'
  const activeClass = active
    ? `bg-[var(--foreground)]/5 font-medium ${textClass}`
    : textClass

  if (hasChildren) {
    return (
      <button
        type="button"
        data-nav-row
        onClick={() => {
          onDrillInto(item)
        }}
        className={`${rowClass} ${activeClass}`}
      >
        <item.icon className="size-[18px] shrink-0 opacity-70" />
        <span className="flex-1 text-left truncate">{label}</span>
        <ChevronRight
          aria-hidden
          className="size-4 text-[var(--muted-foreground)]"
        />
      </button>
    )
  }

  if (item.action) {
    return (
      <button
        type="button"
        data-nav-row
        onClick={() => {
          if (item.action === 'create-review-page') openCreateReviewPage()
          onSelect()
        }}
        className={`${rowClass} ${activeClass}`}
      >
        <item.icon className="size-[18px] shrink-0 opacity-70" />
        <span className="flex-1 text-left truncate">{label}</span>
      </button>
    )
  }

  if (!href) return null

  if (item.external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        data-nav-row
        onClick={onSelect}
        className={`${rowClass} ${textClass}`}
      >
        <item.icon className="size-[18px] shrink-0 opacity-70" />
        <span className="flex-1 truncate">{label}</span>
      </a>
    )
  }

  return (
    <Link
      to={href}
      data-nav-row
      onClick={onSelect}
      className={`${rowClass} ${activeClass}`}
    >
      <item.icon className="size-[18px] shrink-0 opacity-70" />
      <span className="flex-1 truncate">{label}</span>
    </Link>
  )
}

export function MobileNav({
  primary,
  secondary,
  ctx,
  themeClassName = 'new-ui-theme',
}: {
  primary: NavItem[]
  secondary: NavItem[]
  ctx: NavContext
  /** Theme scope for the portaled chrome — old UI passes `old-ui-nav` too. */
  themeClassName?: string
}) {
  const [open, setOpen] = useState(false)
  // Drill *stack* of section ids so nested groups (Wi-Fi → SnapGuard) drill in
  // to arbitrary depth and Back pops exactly one level.
  const [drillStack, setDrillStack] = useState<string[]>([])
  const [search, setSearch] = useState('')
  // The account view is a sibling of the nav list, not a nav section — it's
  // kept out of `drillStack` (which holds real NavItem ids).
  const [accountOpen, setAccountOpen] = useState(false)
  const { pathname } = useLocation()
  const user = useUser()
  const { sections: accountSections, dialogs: accountDialogs } = useAccountMenu(
    ctx.orgId
  )
  const { trigger } = useWebHaptics()
  const lastPathnameRef = useRef(pathname)
  // Discounts, Square Loyalty and Customer Groups are Square-entity-scoped,
  // not venue-scoped: swap the venue pill for the Square entity picker here
  // too (mirrors the desktop top bar).
  const onDiscounts =
    Boolean(ctx.orgId) &&
    (pathname === `/${ctx.orgId}/square/discounts` ||
      pathname === `/${ctx.orgId}/square/loyalty` ||
      pathname === `/${ctx.orgId}/square/customer-groups`)
  // Loyalty is scheme-scoped, so the scheme picker joins the org/venue pills
  // here, the same rule the desktop top bar uses (`old-top-bar`). Matched on
  // true loyalty-v2 paths only (`/:orgId/loyalty/...` or
  // `/:orgId/venues/:serial/loyalty/...`): a bare "does 'loyalty' appear
  // anywhere" check would also fire on the unrelated, entity-scoped Square
  // Loyalty settings page at `/:orgId/square/loyalty`.
  const loyaltySegs = pathname.split('/').filter(Boolean)
  const inLoyalty =
    loyaltySegs[1] === 'loyalty' ||
    (loyaltySegs[1] === 'venues' && loyaltySegs[3] === 'loyalty')

  // When a sheet is open the floating bottom-pill menu would sit over the
  // form's primary action. Step the chrome aside while any sheet is up — the
  // user is focused on that surface anyway. Covers tailwind-v4 Sheets (via the
  // shared store) and JosefSheet (which portals a `.josef-sheet-overlay` to
  // <body>, detected here).
  const v4SheetOpen = useAnySheetOpen()
  const josefSheetOpen = useJosefSheetOpen()
  // The voice orb stands the chrome aside for the same reason a sheet does:
  // it occupies the pill's corner and it is the only thing being used.
  const supportVoiceOpen = useSupportVoiceOpen()
  const anySheetOpen = v4SheetOpen || josefSheetOpen || supportVoiceOpen

  const allNav = useMemo(() => [...primary, ...secondary], [primary, secondary])
  const drilledId = drillStack[drillStack.length - 1] ?? null
  const drilledSection = drilledId ? findNavItem(allNav, drilledId) : null

  // Prefetch a drilled section's sub-item route chunks (deferred) so tapping
  // one renders instantly instead of waiting on a cold code-split chunk.
  useEffect(() => {
    if (!drilledSection) return
    const id = window.setTimeout(() => {
      for (const child of drilledSection.children ?? []) {
        if (child.external || !isNavItemVisible(child, ctx)) continue
        preloadRouteByHref(resolveHref(child, ctx))
      }
    }, 150)
    return () => {
      window.clearTimeout(id)
    }
  }, [drilledSection, ctx])

  const openNav = useCallback(() => {
    trigger(HAPTIC_OPEN)
    // Land on the deepest section whose subtree matches the current URL —
    // users expect "where I am now" to be where the menu lands, not the root
    // list. Returns the full ancestor path so Back pops level by level.
    const matches = (href: string | undefined) =>
      Boolean(href) && (pathname === href || pathname.startsWith(`${href}/`))
    const path = findActivePath(allNav, matches, (item) =>
      resolveHref(item, ctx)
    )
    setDrillStack(path)
    setAccountOpen(false)
    setOpen(true)
  }, [trigger, pathname, ctx, allNav])

  const closeNav = useCallback(() => {
    trigger(HAPTIC_CLOSE)
    setOpen(false)
    setDrillStack([])
    setAccountOpen(false)
    setSearch('')
  }, [trigger])

  const toggleNav = useCallback(() => {
    if (open) closeNav()
    else openNav()
  }, [open, openNav, closeNav])

  /**
   * Hold the pill to talk.
   *
   * Mobile has no cmd+/, and the orb is meant to appear only when somebody
   * asks for it, so there is no microphone button in the chrome to press.
   * The pill is already bottom centre, exactly where the orb comes up, and
   * it already stands aside while the orb is live (`useSupportVoiceOpen`
   * above): holding it IS the pill becoming the orb. Rules and timings are
   * in `support-dock/long-press.ts`.
   *
   * Gated on the nav's OWN resolved subject (a plain `can(ctx.access, ...)`
   * over the subject `useNavCtx()` already resolved) rather than
   * `useCan()`/`AccessProvider`. Those are two SEPARATE subject-resolution
   * paths over the same policy function and they do not reliably agree: a
   * button in the top bar rendered invisible under `useCan()` while the nav
   * item beside it, gated on the same capability via `ctx.access`, showed
   * up fine. This gesture has to match the Support nav item sitting a few
   * lines away, so it reuses the nav's own subject. With no capability the
   * hook arms nothing and the pill is an ordinary button, rather than a
   * gesture that buzzes and does nothing.
   */
  const canTalkToSupport = ctx.access ? can(ctx.access, 'nav.support') : false

  const startSupportVoice = useCallback(() => {
    trigger(HAPTIC_VOICE)
    openSupportDock({ voice: true })
  }, [trigger])

  const pillPress = useLongPress({
    onLongPress: canTalkToSupport ? startSupportVoice : undefined,
    onTap: toggleNav,
  })

  const drillInto = useCallback(
    (item: NavItem) => {
      trigger(HAPTIC_DRILL)
      setDrillStack((s) => [...s, item.id])
    },
    [trigger]
  )

  const popDrill = useCallback(() => {
    trigger(HAPTIC_BACK)
    setDrillStack((s) => s.slice(0, -1))
  }, [trigger])

  const openAccount = useCallback(() => {
    trigger(HAPTIC_DRILL)
    setAccountOpen(true)
  }, [trigger])

  const closeAccount = useCallback(() => {
    trigger(HAPTIC_BACK)
    setAccountOpen(false)
  }, [trigger])

  const handleSelect = useCallback(() => {
    trigger(HAPTIC_TAP)
    setOpen(false)
    setDrillStack([])
    setAccountOpen(false)
    setSearch('')
  }, [trigger])

  // Auto-close on route change.
  useEffect(() => {
    if (lastPathnameRef.current === pathname) return
    lastPathnameRef.current = pathname
    setOpen(false)
    setDrillStack([])
    setAccountOpen(false)
    setSearch('')
  }, [pathname])

  // Escape closes.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeNav()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [open, closeNav])

  // Root list items — same primary + secondary org nav regardless of whether
  // a venue is selected. Each item carries the venue forward via its own
  // `venueTo` when an activeSerial is present in the URL.
  const rootItems: NavItem[] = allNav

  const filtered = useMemo(() => {
    if (!search) return rootItems
    const q = search.toLowerCase()
    return rootItems.filter(
      (i) =>
        isNavItemVisible(i, ctx) &&
        resolveLabel(i, ctx).toLowerCase().includes(q)
    )
  }, [rootItems, search, ctx])

  return (
    <>
      {/* Account dialogs (PIN auth, identity verification) live outside the
          panel's AnimatePresence so they survive the panel closing. */}
      {accountDialogs}

      {/* Dimmed backdrop while open. Tap to close. */}
      <AnimatePresence>
        {open ? (
          <motion.div
            key="mobile-nav-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[55] bg-black/40 backdrop-blur-[2px] md:hidden"
            onClick={closeNav}
            aria-hidden
          />
        ) : null}
      </AnimatePresence>

      {/* Top floating pills — only while open. Org switcher + venue switcher. */}
      <AnimatePresence>
        {open ? (
          <motion.div
            key="mobile-nav-top-pills"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ type: 'spring', stiffness: 500, damping: 40 }}
            className={`${themeClassName} pointer-events-none fixed left-0 right-0 top-0 z-[60] flex items-start justify-between gap-2 px-3 md:hidden`}
            style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}
          >
            <div className="pointer-events-auto flex min-w-0 items-center gap-2">
              <div className="flex min-w-0 max-w-[9.5rem] items-center overflow-hidden rounded-full border border-black/10 dark:border-white/10 bg-[var(--background)]/90 backdrop-blur-md shadow-sm [&_.combobox-trigger]:w-full [&_.combobox-trigger]:min-w-0 [&_.combobox-trigger]:px-2.5 [&_.combobox-trigger]:py-1.5">
                <OrgSwitcher />
              </div>
              {/* Venue switcher stays visible regardless of whether a serial
                  is already in the URL — operators routinely jump between
                  venues. On the Square discounts page (entity-scoped, not
                  venue-scoped) swap it for the Square entity picker, mirroring
                  the desktop top bar. */}
              <div className="flex min-w-0 max-w-[9.5rem] items-center overflow-hidden rounded-full border border-black/10 dark:border-white/10 bg-[var(--background)]/90 backdrop-blur-md shadow-sm [&_.combobox-trigger]:min-w-0 [&_.combobox-trigger]:px-2.5 [&_.combobox-trigger]:py-1.5">
                {onDiscounts ? (
                  <SquareEntityPicker orgId={ctx.orgId} />
                ) : (
                  <VenueSwitcher />
                )}
              </div>
              {inLoyalty ? (
                <div className="flex min-w-0 max-w-[9.5rem] items-center overflow-hidden rounded-full border border-black/10 dark:border-white/10 bg-[var(--background)]/90 backdrop-blur-md shadow-sm [&_.combobox-trigger]:min-w-0 [&_.combobox-trigger]:px-2.5 [&_.combobox-trigger]:py-1.5">
                  <OldSchemePicker />
                </div>
              ) : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Floating nav panel — appears above the bottom bar when open. */}
      <AnimatePresence>
        {open ? (
          <motion.div
            key="mobile-nav-panel"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 480, damping: 42 }}
            className={`${themeClassName} fixed left-3 right-3 z-[58] flex flex-col overflow-hidden rounded-2xl border border-black/10 dark:border-white/10 bg-[var(--background)]/95 backdrop-blur-xl shadow-2xl md:hidden`}
            style={{
              // Panel bottom sits at the vertical centre of the find pill,
              // so the pill's top half overlaps the panel ("half behind"
              // the float) and the bottom half pokes out below.
              bottom: 'calc(env(safe-area-inset-bottom) + 3.25rem)',
              height:
                'calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 6.75rem)',
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
          >
            <div
              data-scroll-container
              className="flex-1 overflow-y-auto overscroll-contain p-2 pb-8"
            >
              <AnimatePresence mode="wait" initial={false}>
                {accountOpen ? (
                  <motion.div
                    key="account"
                    initial={{ opacity: 0, x: 14 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 14 }}
                    transition={{ duration: 0.16, ease: 'easeOut' }}
                    className="flex flex-col gap-0.5"
                  >
                    <button
                      type="button"
                      data-nav-row
                      onClick={closeAccount}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[var(--muted-foreground)] active:bg-[var(--foreground)]/5"
                    >
                      <ArrowLeft className="size-4" />
                      <span>Account</span>
                    </button>
                    <div className="flex flex-col gap-0.5 px-3 pb-1">
                      <span className="truncate text-sm font-medium text-[var(--foreground)]">
                        {user?.first} {user?.last}
                      </span>
                      <span className="truncate text-xs text-[var(--muted-foreground)]">
                        {user?.email}
                      </span>
                    </div>
                    {accountSections.map((section) => (
                      <div key={section.id} className="contents">
                        <Separator className="my-1" />
                        {section.items.map((item) => (
                          <AccountRow
                            key={item.id}
                            item={item}
                            onNavigate={handleSelect}
                            onAction={() => {
                              trigger(HAPTIC_TAP)
                            }}
                          />
                        ))}
                      </div>
                    ))}
                  </motion.div>
                ) : null}
                {!accountOpen && drilledSection ? (
                  <motion.div
                    key={`drill-${drilledSection.id}`}
                    initial={{ opacity: 0, x: 14 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 14 }}
                    transition={{ duration: 0.16, ease: 'easeOut' }}
                    className="space-y-0.5"
                  >
                    <button
                      type="button"
                      data-nav-row
                      onClick={popDrill}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[var(--muted-foreground)] active:bg-[var(--foreground)]/5"
                    >
                      <ArrowLeft className="size-4" />
                      <span>{resolveLabel(drilledSection, ctx)}</span>
                    </button>
                    <Separator className="my-1" />
                    {(drilledSection.children ?? [])
                      .filter(
                        (child) =>
                          isNavItemVisible(child, ctx) &&
                          !isHiddenOnMobile(child, ctx)
                      )
                      .map((child, i) => {
                        const showSeparator = child.separatorBefore && i > 0
                        return (
                          <div key={child.id} className="contents">
                            {showSeparator ? (
                              <Separator className="my-1" />
                            ) : null}
                            <MobileNavRow
                              item={child}
                              ctx={ctx}
                              activePathname={pathname}
                              exactActive
                              onSelect={handleSelect}
                              onDrillInto={drillInto}
                            />
                          </div>
                        )
                      })}
                  </motion.div>
                ) : null}
                {!accountOpen && !drilledSection ? (
                  <motion.div
                    key="root"
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={{ duration: 0.16, ease: 'easeOut' }}
                    className="space-y-0.5"
                  >
                    {filtered
                      .filter(
                        (item) =>
                          isNavItemVisible(item, ctx) &&
                          !isHiddenOnMobile(item, ctx)
                      )
                      .map((item, i) => (
                        <div key={item.id} className="contents">
                          {item.separatorBefore && i > 0 ? (
                            <Separator className="my-1.5" />
                          ) : null}
                          <MobileNavRow
                            item={item}
                            ctx={ctx}
                            activePathname={pathname}
                            onSelect={handleSelect}
                            onDrillInto={drillInto}
                          />
                        </div>
                      ))}
                    {filtered.length === 0 ? (
                      <div className="px-3 py-6 text-center text-sm text-[var(--muted-foreground)]">
                        No matches
                      </div>
                    ) : null}
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
            {/* Footer pinned to the bottom of the panel: the account row —
                THE way to reach Profile / Billing / Support / Log out on a
                phone, since the sidebar footer that hosts the desktop menu
                can't be opened at that width. Extra bottom padding so the
                floating pill (which sits on top of the panel's bottom)
                doesn't cover it. */}
            {!drilledSection && !search && !accountOpen ? (
              <div className="shrink-0 border-t border-black/5 dark:border-white/5 p-2 pb-8">
                <button
                  type="button"
                  onClick={openAccount}
                  aria-label="Account"
                  className={`${ROW_CLASS} text-[var(--foreground)]/90`}
                >
                  <AccountAvatar
                    first={user?.first}
                    last={user?.last}
                    email={user?.email}
                    className="size-6"
                  />
                  <span className="flex-1 truncate text-left">
                    {user?.first ?? 'Account'}
                  </span>
                  <ChevronRight
                    aria-hidden
                    className="size-4 text-[var(--muted-foreground)]"
                  />
                </button>
              </div>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Bottom fade — content dissolves into the canvas as it passes
          behind the resting pill instead of hard-cutting. A fixed viewport
          overlay (fades any scroller underneath — page or planner grid),
          pointer-events-none so taps fall through, and only while the menu
          is closed (an open menu's own backdrop covers this zone). Gentle
          eased alpha, NO solid shelf, so it reads as a soft dissolve rather
          than a docked strip; fades to var(--background) so it flips
          light/dark for free. */}
      <AnimatePresence>
        {anySheetOpen || open ? null : (
          <motion.div
            key="mobile-nav-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className={`${themeClassName} pointer-events-none fixed inset-x-0 bottom-0 z-[57] md:hidden`}
            style={{
              height: 'calc(9rem + env(safe-area-inset-bottom))',
              background:
                'linear-gradient(to top,' +
                ' color-mix(in srgb, var(--background) 96%, transparent) 0%,' +
                ' color-mix(in srgb, var(--background) 88%, transparent) 14%,' +
                ' color-mix(in srgb, var(--background) 66%, transparent) 36%,' +
                ' color-mix(in srgb, var(--background) 38%, transparent) 60%,' +
                ' color-mix(in srgb, var(--background) 16%, transparent) 80%,' +
                ' transparent 100%)',
            }}
            aria-hidden
          />
        )}
      </AnimatePresence>

      {/* Persistent floating bottom pill — [≡ Menu] / [× Close]. Find
          search coming back in when that implementation is ready.
          Hidden while any tailwind-v4 Sheet is open so the form's primary
          action keeps the bottom edge to itself. */}
      <AnimatePresence>
        {anySheetOpen ? null : (
          <motion.div
            key="mobile-nav-pill"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ type: 'spring', stiffness: 480, damping: 42 }}
            className={`${themeClassName} pointer-events-none fixed left-0 right-0 z-[60] flex justify-center px-3 md:hidden`}
            style={{ bottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}
          >
            <button
              type="button"
              aria-label={open ? 'Close menu' : 'Open menu'}
              title={
                canTalkToSupport ? 'Hold to talk to the assistant' : undefined
              }
              {...pillPress}
              className="pointer-events-auto inline-flex h-11 touch-manipulation select-none items-center gap-2 rounded-xl border border-black/10 dark:border-white/10 bg-[var(--background)]/90 backdrop-blur-xl px-4 text-sm font-medium text-[var(--foreground)] shadow-lg"
            >
              {open ? (
                <X className="size-4" />
              ) : (
                <MenuLines className="size-4 translate-y-[1px]" />
              )}
              <span>{open ? 'Close' : 'Menu'}</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
